// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { featureFlags } from '../../config/featureFlags';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Alert, Spinner } from '@loa/ui-components';
import {
  relationshipCashflowApi,
  type ProjectionAssumptions,
  type RelationshipCashflow,
} from '../../api/relationshipCashflowApi';
import { ScenarioControls } from './ScenarioControls';

interface Props {
  profileId: string;
  /** External refresh signal — bump to refetch (shared with the dashboard tiles). */
  refreshSignal?: number;
}

/** Editable assumption fields, in display order, with friendly labels. */
const ASSUMPTION_FIELDS: Array<{ key: keyof ProjectionAssumptions; label: string; suffix: string }> = [
  { key: 'priceInflationRate', label: 'Price inflation', suffix: '%' },
  { key: 'earningsGrowthRate', label: 'Earnings growth', suffix: '%' },
  { key: 'statePensionAnnual', label: 'State pension (annual)', suffix: '' },
  { key: 'pensionDrawdownRate', label: 'Pension drawdown', suffix: '%' },
  { key: 'expenditureAtRetirementFactor', label: 'Expenditure at retirement', suffix: '×' },
  { key: 'defaultSavingsGrowthRate', label: 'Default savings growth', suffix: '%' },
  { key: 'defaultInvestmentGrowthRate', label: 'Default investment growth', suffix: '%' },
  { key: 'defaultPropertyGrowthRate', label: 'Default property growth', suffix: '%' },
  { key: 'defaultCollectableGrowthRate', label: 'Default collectable growth', suffix: '%' },
  { key: 'childBenefitMonthly', label: 'Child Benefit (per child, monthly)', suffix: '€' },
];

const FIELD_LABELS: Record<string, string> = Object.fromEntries(
  ASSUMPTION_FIELDS.map((f) => [f.key, f.label]),
);

/** Trigger a browser download of CSV text. */
function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function money(value: number, currency: string | null): string {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: currency ?? 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

export function RelationshipCashflowCard({ profileId, refreshSignal }: Props) {
  const navigate = useNavigate();
  const [data, setData] = useState<RelationshipCashflow | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [horizonInput, setHorizonInput] = useState('');
  const [savingHorizon, setSavingHorizon] = useState(false);

  const exportCsv = () => {
    if (exporting) return;
    setExporting(true);
    setError(null);
    relationshipCashflowApi
      .exportCsv(profileId)
      .then((csv) => downloadCsv(csv, `cashflow-${profileId}.csv`))
      .catch(() => setError('Could not export the cashflow report.'))
      .finally(() => setExporting(false));
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    relationshipCashflowApi
      .getCashflow(profileId, scenarioId)
      .then((d) => {
        if (d === null) setDisabled(true);
        else setData(d);
      })
      .catch(() => setError('Could not load the cashflow projection.'))
      .finally(() => setLoading(false));
  }, [profileId, scenarioId]);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  const saveHorizon = () => {
    const years = Number(horizonInput);
    if (savingHorizon || horizonInput.trim() === '' || Number.isNaN(years) || years < 1 || years > 100) {
      setError('Enter an outlook of 1–100 years.');
      return;
    }
    setSavingHorizon(true);
    setError(null);
    relationshipCashflowApi
      .setHorizon(profileId, years)
      .then(() => load())
      .catch(() => setError('Could not update the outlook horizon.'))
      .finally(() => setSavingHorizon(false));
  };

  if (disabled) return null;

  return (
    <div
      data-testid="relationship-cashflow-card"
      className="flex flex-col w-full rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)] pt-lg pr-md pb-lg pl-md gap-md mb-lg"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-black text-body-01 font-medium leading-normal font-sans">
            Cashflow &amp; wealth
          </div>
          <div className="flex items-center gap-[6px] text-[13px] text-[#8A8A8A]">
            <span>
              {data && data.gross === false ? 'After-tax' : 'Pre-tax'} · today&apos;s money · outlook
            </span>
            <input
              type="number"
              min={1}
              max={100}
              data-testid="fp-cashflow-horizon"
              aria-label="Outlook horizon (years)"
              defaultValue={data?.horizonYears ?? 20}
              onChange={(e) => setHorizonInput(e.target.value)}
              className="w-[56px] rounded-[6px] border-[1.5px] border-[#BDB8FF] px-[6px] py-[2px] text-[13px] text-black"
            />
            <span>years</span>
            {horizonInput.trim() !== '' && Number(horizonInput) !== (data?.horizonYears ?? 20) && (
              <button
                type="button"
                data-testid="fp-cashflow-horizon-save"
                onClick={saveHorizon}
                disabled={savingHorizon}
                className="rounded-[6px] bg-purple-primary px-sm py-[2px] text-body-04 text-white disabled:opacity-60"
              >
                Apply
              </button>
            )}
          </div>
        </div>
        {data && (
          <div className="flex flex-wrap items-center gap-sm">
            {featureFlags.cashflowWorkspace ? (
              // Scenario management graduated to the Cashflow Modelling workspace
              // (docs/specs/cashflow-modelling-tab-prd.md R6) — one entry point.
              <button
                type="button"
                data-testid="fp-cashflow-open-workspace"
                onClick={() => navigate(`/relationships/${profileId}?tab=cashflow`)}
                className="rounded-lg border border-purple-primary bg-white px-sm py-xs text-body-03 text-purple-primary hover:bg-lilac-06"
              >
                Open Cashflow Modelling
              </button>
            ) : (
              <ScenarioControls
                profileId={profileId}
                selectedScenarioId={scenarioId}
                onSelect={setScenarioId}
              />
            )}
            <button
              type="button"
              data-testid="fp-cashflow-export"
              onClick={exportCsv}
              disabled={exporting}
              className="rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[12px] py-[6px] text-body-03 text-purple-primary hover:bg-lilac-06 disabled:opacity-60"
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <button
              type="button"
              data-testid="fp-cashflow-assumptions-toggle"
              onClick={() => setEditing((v) => !v)}
              className="rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[12px] py-[6px] text-body-03 text-purple-primary hover:bg-lilac-06"
            >
              {editing ? 'Hide assumptions' : 'Assumptions'}
            </button>
          </div>
        )}
      </div>

      {loading && <Spinner />}
      {error && <Alert variant="error">{error}</Alert>}

      {data && data.missingAssumptions.length > 0 && (
        <Alert variant="warning" data-testid="fp-cashflow-missing">
          This projection is partial — set{' '}
          {data.missingAssumptions.map((k) => FIELD_LABELS[k] ?? k).join(', ')} to complete it.
        </Alert>
      )}

      {data && editing && (
        <AssumptionsEditor
          profileId={profileId}
          assumptions={data.assumptions}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      )}

      {data && (
        <ResponsiveContainer width="100%" height={360}>
          {/* Generous top margin so the event marker labels (position="top") aren't clipped. */}
          <ComposedChart data={data.years} margin={{ left: 24, right: 24, top: 36, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
            <XAxis dataKey="calendarYear" tick={{ fontSize: 12 }} />
            <YAxis
              yAxisId="flow"
              tick={{ fontSize: 12 }}
              width={72}
              tickFormatter={(v) => money(Number(v), data.currency)}
            />
            <YAxis
              yAxisId="wealth"
              orientation="right"
              tick={{ fontSize: 12 }}
              width={72}
              tickFormatter={(v) => money(Number(v), data.currency)}
            />
            <Tooltip
              formatter={(value) => money(Number(value), data.currency)}
              labelFormatter={(label) => `Year ${label}`}
            />
            <Legend />
            {/* Income bar = Net income (green) stacked with Tax (amber); total = gross. */}
            <Bar yAxisId="flow" dataKey="netIncome" name="Net income" stackId="income" fill="#1E8E4E" />
            <Bar yAxisId="flow" dataKey="tax" name="Tax" stackId="income" fill="#E59866" />
            <Bar yAxisId="flow" dataKey="totalExpenditure" name="Expenditure" fill="#C0392B" />
            <Line yAxisId="flow" type="monotone" dataKey="surplus" name="Surplus" stroke="#7C5CFC" strokeWidth={2} dot={false} />
            <Line yAxisId="wealth" type="monotone" dataKey="netWorth" name="Net worth" stroke="#2D6CDF" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            {data.events.map((ev, i) => (
              <ReferenceLine
                key={`${ev.type}-${ev.year}-${i}`}
                yAxisId="flow"
                x={(data.years[ev.year]?.calendarYear) ?? undefined}
                stroke="#B0A8FF"
                strokeDasharray="2 2"
                label={{ value: ev.label, position: 'top', fontSize: 10, fill: '#6B6B6B' }}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function AssumptionsEditor({
  profileId,
  assumptions,
  onSaved,
}: {
  profileId: string;
  assumptions: ProjectionAssumptions;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      ASSUMPTION_FIELDS.map((f) => [f.key, assumptions[f.key] == null ? '' : String(assumptions[f.key])]),
    ),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    const body = Object.fromEntries(
      ASSUMPTION_FIELDS.map((f) => {
        const raw = form[f.key]?.trim();
        return [f.key, raw === undefined || raw === '' ? null : Number(raw)];
      }),
    ) as unknown as ProjectionAssumptions;
    relationshipCashflowApi
      .setAssumptions(profileId, body)
      .then(() => onSaved())
      .catch(() => setError('Could not save the assumptions.'))
      .finally(() => setSaving(false));
  };

  return (
    <div data-testid="fp-cashflow-assumptions-editor" className="rounded-lg bg-page-bg p-md flex flex-col gap-[12px]">
      <div className="grid grid-cols-2 gap-[12px] md:grid-cols-3">
        {ASSUMPTION_FIELDS.map((f) => (
          <label key={f.key} className="flex flex-col gap-xs text-[13px] text-grey-04">
            <span>
              {f.label} {f.suffix && <span className="text-[#8A8A8A]">({f.suffix})</span>}
            </span>
            <input
              type="number"
              data-testid={`fp-assumption-${f.key}`}
              value={form[f.key] ?? ''}
              onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
              className="rounded-lg border-[1.5px] border-[#E1E1E1] px-[10px] py-[6px] text-body-03"
            />
          </label>
        ))}
      </div>
      {error && <Alert variant="error">{error}</Alert>}
      <div>
        <button
          type="button"
          data-testid="fp-cashflow-assumptions-save"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-purple-primary px-md py-sm text-body-03 text-white disabled:opacity-60"
        >
          Save assumptions
        </button>
      </div>
    </div>
  );
}
